import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Eye, Gem, Building2, Users, Briefcase, Globe, Smile, Award,
  ShieldCheck, Lightbulb, BadgeCheck, TrendingUp, Headphones,
  Rocket, Code2, Cloud, PenTool, Mail, Phone,
  ArrowRight, ChevronRight,
} from 'lucide-react';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* Resize the hero image with the values below. On desktop it's an ABSOLUTE overlay,
   so making it bigger only overlaps — the text and everything below never move.
   (If you enlarge it a lot and it clips, bump the `lg:min-h-[520px]` on its wrapper div.) */
const HERO_IMAGE = {
  desktopWidth: '750px',   // ← increase this to make the image bigger on desktop
  desktopHeight: 'auto',  // ← 'auto' keeps the ratio, or set e.g. '500px'
  offsetX: '0px',         // ← horizontal nudge (negative = push right / off edge, positive = pull left)
  offsetY: '0px',         // ← vertical nudge, e.g. '-20px'
};

/* ============================== DATA ============================== */

const MVP = [
  { Icon: Target, title: 'Our Mission', desc: 'To empower businesses with innovative software solutions that drive efficiency, productivity and sustainable growth.' },
  { Icon: Eye, title: 'Our Vision', desc: 'To be a global technology partner recognized for delivering smart, reliable and future-ready digital solutions.' },
  { Icon: Gem, title: 'Our Promise', desc: 'We are committed to quality, transparency, and long-term partnerships with every client we work with.' },
];

const STATS = [
  { Icon: Building2, value: '2024', label: 'Established' },
  { Icon: Users, value: '10+', label: 'Team Members' },
  { Icon: Briefcase, value: '50+', label: 'Projects Delivered' },
  { Icon: Globe, value: '2+', label: 'Countries Served' },
  { Icon: Smile, value: '99%', label: 'Client Satisfaction' },
  { Icon: Award, value: '18+', label: 'Industries Served' },
];

const WHY = [
  { Icon: ShieldCheck, title: 'Integrity', desc: 'We believe in honest communication and ethical business practices.' },
  { Icon: Users, title: 'Client First', desc: "Our clients' success is our success. We build lasting relationships." },
  { Icon: Lightbulb, title: 'Innovation', desc: 'We embrace new ideas and technologies to deliver better solutions.' },
  { Icon: BadgeCheck, title: 'Quality', desc: 'We follow best practices to ensure top-quality software delivery.' },
  { Icon: TrendingUp, title: 'Growth', desc: 'We grow together with our clients, teams and communities.' },
  { Icon: Headphones, title: 'Support', desc: 'We provide dedicated support and maintenance every step of the way.' },
];

const JOURNEY = [
  { Icon: Rocket, color: '#3284c7', year: '2024', title: 'The Beginning', desc: 'SmartEdz was founded with a vision to deliver smart and affordable software solutions.' },
  { Icon: Users, color: '#f29132', year: '2024', title: 'Team Expansion', desc: 'We expanded our team and delivered our first set of enterprise solutions.' },
  { Icon: Code2, color: '#16a34a', year: '2025', title: 'Product Innovation', desc: 'Launched multiple ERP products for education and business management.' },
  { Icon: Globe, color: '#9333ea', year: '2026', title: 'Global Reach', desc: 'Expanded our services to international markets and served clients worldwide.' },
  { Icon: Cloud, color: '#3284c7', year: '2027', title: 'Cloud & AI', desc: 'Integrated cloud solutions and AI technologies into our platforms.' },
  { Icon: Award, color: '#f29132', year: '2028', title: 'Milestone Achieved', desc: '500+ projects delivered with 99% client satisfaction.' },
  { Icon: Rocket, color: '#16a34a', year: 'Future', title: 'Future Forward', desc: 'Continuing our journey to build smarter, intelligent and impactful digital solutions.' },
];

const TEAM_STATS = [
  { Icon: Briefcase, color: '#3284c7', value: '10+', label: 'Developers' },
  { Icon: PenTool, color: '#16a34a', value: '5+', label: 'Designers' },
  { Icon: ShieldCheck, color: '#9333ea', value: '5+', label: 'QA Engineers' },
  { Icon: Users, color: '#f59e0b', value: '2+', label: 'Project Managers' },
];

/* ============================== HELPERS ============================== */

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ============================== PAGE ============================== */

const AboutUs = () => {
  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        /* Hero image: absolute overlay on desktop so resizing it never shifts the layout */
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 600px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-8 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="se-fade-up">
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">About Us</span>
              </nav>

              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">About SmartEdz</span>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.08] tracking-tight" style={{ color: '#13294b' }}>
                Building Technology.<br />
                <span className="bg-gradient-to-r from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Building Trust.</span>
              </h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                SmartEdz is a leading software development company delivering intelligent, secure and scalable software
                solutions that help businesses transform, grow and succeed in the digital era.
              </p>

              <div className="mt-7 flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white bg-primary shadow-lg shadow-primary/30 hover:brightness-110 transition">
                  Our Story <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-primary bg-white ring-1 ring-primary/40 hover:bg-primary/5 transition">
                  Meet Our Team <Users className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="se-fade-up relative lg:min-h-[520px]" style={{ animationDelay: '.15s' }}>
              <img
                src="/AboutUs_Img.png"
                alt="SmartEdz office"
                className="se-hero-img w-full h-auto max-w-[900px] mx-auto block select-none drop-shadow-2xl"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= MISSION / VISION / PROMISE ================= */}
      <section className={`${CONTAINER} pb-12`}>
        <Reveal className="rounded-2xl bg-gradient-to-r from-[#0A1B3D] via-[#0f2e6b] to-[#123a86] px-6 sm:px-10 py-10">
          <div className="grid md:grid-cols-3 gap-8 md:divide-x divide-white/10">
            {MVP.map((m) => (
              <div key={m.title} className="flex gap-4 md:px-4">
                <div className="w-12 h-12 rounded-full bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
                  <m.Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1.5">{m.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= STATS BAR ================= */}
      <section className={`${CONTAINER} pb-14`}>
        <Reveal className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-6 lg:divide-x divide-slate-100">
            {STATS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 justify-center px-2">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <s.Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xl font-extrabold leading-none" style={{ color: '#13294b' }}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= WHY SMARTEDZ ================= */}
      <section className={`${CONTAINER} pb-14`}>
        <Reveal><h2 className="text-center text-3xl sm:text-4xl font-extrabold tracking-tight mb-10" style={{ color: '#13294b' }}>Why SmartEdz?</h2></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 50}>
              <div className="group h-full bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all">
                  <w.Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-slate-800 mb-1.5">{w.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= OUR JOURNEY ================= */}
      <section className="bg-[#f9fbfe] py-14">
        <div className={CONTAINER}>
          <Reveal>
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Our Journey</h2>
              <p className="mt-2 text-slate-500">A journey of passion, innovation and continuous growth.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-8">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.year} delay={i * 60}>
                <div className="relative text-center xl:after:content-[''] xl:after:absolute xl:after:top-8 xl:after:left-[62%] xl:after:w-[76%] xl:after:border-t-2 xl:after:border-dashed xl:after:border-slate-300 xl:last:after:hidden">
                  <div className="relative z-10 w-16 h-16 mx-auto rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-3" style={{ backgroundColor: `${j.color}12` }}>
                    <j.Icon className="w-7 h-7" style={{ color: j.color }} />
                  </div>
                  <div className="text-sm font-extrabold" style={{ color: '#13294b' }}>{j.year}</div>
                  <div className="text-xs font-bold text-primary mb-1.5">{j.title}</div>
                  <p className="text-[11px] text-slate-500 leading-relaxed max-w-[170px] mx-auto">{j.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= OUR TEAM ================= */}
      <section className={`${CONTAINER} py-14`}>
        <Reveal className="rounded-2xl bg-[#f5f9fd] ring-1 ring-slate-200 p-6 sm:p-8">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200 aspect-[4/3]">
              <img
                src="/AboutUs_Team.png"
                alt="SmartEdz team"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold mb-3" style={{ color: '#13294b' }}>Our Team</h2>
              <p className="text-slate-500 leading-relaxed mb-5">
                Our strength lies in our people. SmartEdz is powered by skilled developers, designers, analysts and
                domain experts who are passionate about technology and committed to delivering excellence.
              </p>
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-primary bg-white ring-1 ring-primary/30 hover:bg-primary/5 transition">
                Meet Our Team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {TEAM_STATS.map((t) => (
                <div key={t.label} className="rounded-xl p-4 ring-1 ring-slate-100" style={{ backgroundColor: `${t.color}0F` }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${t.color}1F` }}>
                    <t.Icon className="w-5 h-5" style={{ color: t.color }} />
                  </div>
                  <div className="text-xl font-extrabold" style={{ color: t.color }}>{t.value}</div>
                  <div className="text-xs text-slate-500">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ================= CTA BANNER ================= */}
      <section className={`${CONTAINER} pb-16`}>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#123a86] via-[#0f2e6b] to-[#123a86] px-6 sm:px-10 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5 text-center lg:text-left">
              <div className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
                <Mail className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-white">Let's Build Something Amazing Together</h3>
                <p className="text-slate-300 mt-1">Have a project in mind? We'd love to hear from you.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-primary bg-white hover:bg-slate-100 transition whitespace-nowrap">
                <Phone className="w-4 h-4" /> Schedule a Call
              </Link>
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white ring-1 ring-white/40 hover:bg-white/10 transition whitespace-nowrap">
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;